import { useCallback, useEffect, useState } from 'react';
import {
  fetchAdCampaigns,
  updateAdCampaignStatus,
  deleteAdCampaign,
  CAMPAIGN_STATUS_LABELS,
  type AdCampaign,
  type CampaignStatus,
} from '@/lib/ads-campaigns';

const STATUS_OPTIONS: CampaignStatus[] = ['pending', 'approved', 'active', 'completed', 'rejected'];

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-secondary-100 text-secondary-900',
  approved: 'bg-accent-100 text-accent-900',
  active: 'bg-primary-100 text-primary-900',
  completed: 'bg-background-200 text-foreground-700',
  rejected: 'bg-foreground-800 text-foreground-100',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  unpaid: 'Non payé',
  pending: 'Paiement en cours',
  completed: 'Payé',
  cancelled: 'Annulé',
  failed: 'Échoué',
  unknown: 'Inconnu',
};

const PAYMENT_STATUS_STYLES: Record<string, string> = {
  unpaid: 'bg-background-200/70 text-foreground-500',
  pending: 'bg-secondary-100 text-secondary-900',
  completed: 'bg-accent-500/15 text-accent-400',
  cancelled: 'bg-accent-100/10 text-accent-400',
  failed: 'bg-accent-100/10 text-accent-400',
  unknown: 'bg-background-200/70 text-foreground-500',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR');
}

function CampaignsSection() {
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdCampaigns();
      setCampaigns(data);
    } catch {
      setError('Impossible de charger les campagnes. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (id: string, status: CampaignStatus) => {
    const target = campaigns.find((c) => c.id === id);
    if (
      target &&
      (status === 'approved' || status === 'active' || status === 'completed') &&
      target.payment_status !== 'completed'
    ) {
      setError(
        "Cette campagne n'a pas encore été payée. Le paiement doit être confirmé avant de pouvoir la valider ou l'activer.",
      );
      return;
    }

    setUpdatingId(id);
    setError('');
    try {
      await updateAdCampaignStatus(id, status);
      setCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status } : c)),
      );
    } catch {
      setError('Impossible de mettre à jour le statut.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError('');
    try {
      await deleteAdCampaign(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError('Impossible de supprimer cette campagne.');
    } finally {
      setDeletingId(null);
    }
  };

  const pendingCount = campaigns.filter((c) => c.status === 'pending').length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl text-foreground-50">
          Campagnes publicitaires
        </h1>
        <p className="text-sm text-foreground-400 mt-1">
          Demandes de publicité reçues via la page « WorldApeiron Ads ». Gérez le statut et le suivi
          de chaque campagne.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20 mb-4">
          <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
          <p className="text-sm text-accent-400">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold">
          <i className="ri-inbox-line" />
          {pendingCount} en attente
        </span>
        <span className="text-sm text-foreground-400">
          {campaigns.length} campagne{campaigns.length > 1 ? 's' : ''} au total
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-foreground-400">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Chargement des campagnes…
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 bg-background-100 border border-background-200/70 rounded-xl">
          <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-megaphone-line text-2xl text-primary-400" />
          </div>
          <p className="text-foreground-400">
            Aucune campagne publicitaire pour le moment. Les demandes apparaîtront ici dès qu'une
            entreprise souscrit à une offre.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-background-100 border border-background-200/70 rounded-xl p-4"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Bannière */}
                <div className="w-full sm:w-28 h-16 rounded-lg overflow-hidden bg-background-200/60 shrink-0">
                  {campaign.banner_url ? (
                    <img
                      src={campaign.banner_url}
                      alt={`Bannière ${campaign.company_name}`}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-2xl text-foreground-400" />
                    </div>
                  )}
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground-50">{campaign.company_name}</h3>
                    <span className="text-xs font-semibold text-accent-400 bg-accent-500/10 px-2 py-0.5 rounded-full uppercase whitespace-nowrap">
                      {campaign.offer}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        STATUS_STYLES[campaign.status] || 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        PAYMENT_STATUS_STYLES[campaign.payment_status || 'unpaid'] || 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {PAYMENT_STATUS_LABELS[campaign.payment_status || 'unpaid'] || campaign.payment_status}
                    </span>
                    {campaign.paydunya_mode && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-foreground-50/10 text-foreground-400 uppercase whitespace-nowrap">
                        {campaign.paydunya_mode}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-foreground-400">
                    {campaign.manager_name} · {campaign.email} · {campaign.phone}
                  </p>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-foreground-500">
                    {campaign.sector && (
                      <span className="flex items-center gap-1">
                        <i className="ri-briefcase-line" />
                        {campaign.sector}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line" />
                      Début : {formatDate(campaign.start_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-flag-line" />
                      Fin : {formatDate(campaign.end_date)}
                    </span>
                    {campaign.placement && (
                      <span className="flex items-center gap-1">
                        <i className="ri-pin-distance-line" />
                        {campaign.placement}
                      </span>
                    )}
                  </div>

                  {campaign.website && (
                    <a
                      href={campaign.website}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-xs text-primary-400 hover:text-primary-300 mt-1 inline-block truncate max-w-full"
                    >
                      {campaign.website}
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="flex sm:flex-col items-center gap-2 shrink-0">
                  <select
                    value={campaign.status}
                    disabled={updatingId === campaign.id}
                    onChange={(e) =>
                      handleStatusChange(campaign.id, e.target.value as CampaignStatus)
                    }
                    className="px-2.5 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-xs text-foreground-50 focus:outline-none focus:border-primary-400 disabled:opacity-50"
                  >
                    {STATUS_OPTIONS.map((s) => {
                      const requiresPayment =
                        (s === 'approved' || s === 'active' || s === 'completed') &&
                        campaign.payment_status !== 'completed';
                      return (
                        <option key={s} value={s} disabled={requiresPayment}>
                          {CAMPAIGN_STATUS_LABELS[s]}
                          {requiresPayment ? ' (paiement requis)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {campaign.payment_status !== 'completed' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-accent-400 uppercase whitespace-nowrap">
                      <i className="ri-lock-line" />
                      Paiement requis
                    </span>
                  )}
                  <button
                    onClick={() => handleDelete(campaign.id)}
                    disabled={deletingId === campaign.id}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-800/80 text-foreground-100 hover:bg-foreground-700 transition-colors disabled:opacity-50 shrink-0"
                    aria-label="Supprimer la campagne"
                  >
                    {deletingId === campaign.id ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-delete-bin-6-line" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CampaignsSection;